import { useState, useEffect, useCallback } from 'react';
import { fetchPlayerGames } from '../utils/dataFetching';
import { useSeason } from './useSeason';
import type { UsePlayerGamesReturn, PlayerGameSeries } from '../types';

export function usePlayerGames(bowlerId: number | null, seasonYearOverride?: number): UsePlayerGamesReturn {
  const { selectedSeason } = useSeason();
  const seasonYear = seasonYearOverride ?? selectedSeason;
  const [gamesData, setGamesData] = useState<PlayerGameSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchGames = useCallback(async () => {
    if (!bowlerId) {
      setGamesData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await fetchPlayerGames(bowlerId, seasonYear);
      setGamesData(data);
    } catch (err) {
      console.error('Error fetching player games:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch player games'));
    } finally {
      setLoading(false);
    }
  }, [bowlerId, seasonYear]);

  const refetch = async () => {
    await fetchGames();
  };

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  return {
    gamesData,
    loading,
    error,
    refetch,
    clearError: () => setError(null),
    retryLastOperation: refetch,
  };
}